import React, { useCallback, useEffect, useRef } from 'react';

import delIco from 'assets/images/Delete_procedure.svg';
import Icon from 'components/generic/Icon.component';
import RECURRENCE_DESCRIPTION from 'constants/recurrence/recurrenceDescription.const';
import RRPT from 'constants/recurrence/recurrenceRuleParseType.const';
import useTip from 'hooks/useTip.hook';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { formatDayToMonthLongLocale, formatHourAndMinuteFull } from 'utils/date/format.util';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import deRef from 'utils/objects/deRef.util';
import isNotDomElement from 'utils/predicates/dom/isNotDomElement.util';
import recurrenceRuleSimplified from 'utils/recurrence/recurrenceRuleSimplified.util';

import CardModalContent from 'scheduler/components/Card/ModalContent';
import CardModalHeader from 'scheduler/components/Card/ModalHeader';

import CN from './EventModalPatchTest.module.scss';

const CARD_MODAL_HEADER_STYLE = Object.freeze({ backgroundColor: 'rgba(0, 36, 116, 1)' });

const DEFAULT_RECURRENCE = 'Repeats';

const positionElement = (element) => {
  if (isNotDomElement(element)) return;

  const { innerWidth } = window;
  const { right } = element.getBoundingClientRect();
  const { style } = element;

  style.position = 'fixed';
  style.left = 'auto';
  style.top = '26%';
  style.right = innerWidth && innerWidth < right + 100 ? '90px' : 'auto';

  elementScrollTo({ element, behavior: 'instant', block: 'center', inline: 'center' });
};

const EventModalPatchTest = ({ event, onClose, onDeleteEvent, onLoad, onUnload }) => {
  const { val: isPatchTestDeleted, yay: deletePatchTest } = useTip(false);
  const componentRef = useRef(null);

  const { start_time: bgn, end_time: end, client_name: clientName, recurrences, notes } = asObject(event);

  const hasNotes = fromAnyToBool(notes && notes.length > 0);

  const rrs = recurrenceRuleSimplified({ string: recurrences, type: RRPT.mm });
  const displayRecurrence = RECURRENCE_DESCRIPTION[rrs] || DEFAULT_RECURRENCE;

  const displayDate = formatDayToMonthLongLocale(bgn);
  const displayTimeSpan = formatHourAndMinuteFull(end);

  const handleDelete = useCallback(
    () => {
      call('EventModalPatchTest.onClose', onClose);
      call('EventModalPatchTest.onDeleteEvent', onDeleteEvent, { event, all: false });
    },
    [event, onClose, onDeleteEvent],
  );

  useEffect(
    () => {
      positionElement(deRef(componentRef));
      call('EventModalPatchTest.onLoad', onLoad);
      return () => void call('EventModalPatchTest.onUnload', onUnload);
    },
    [onLoad, onUnload],
  );

  return (
    <div data-bem="EventModalPatchTest" ref={componentRef} className="card-modal-wraper animated zoomIn patch">
      <CardModalHeader onClose={onClose} style={CARD_MODAL_HEADER_STYLE}>
        <div className="client-info">
          <button onClick={deletePatchTest} className="btn-delete-patch">
            <img src={delIco} alt="Delete booking" />
            <span>Delete</span>
          </button>
          <div>
            <div className="name">PATCH TEST</div>
            <div className="phone-email">
              <span>{clientName}</span>
            </div>
          </div>
        </div>
        {isPatchTestDeleted && (
          <div className="del-patch-tooltip">
            <span>Delete patch test?</span>
            <div>
              <button onClick={onClose} className="del-no">
                No
              </button>
              <button onClick={handleDelete} className="del-yes">
                Yes
              </button>
            </div>
          </div>
        )}
      </CardModalHeader>
      <CardModalContent>
        <div className={CN.modalPatchTest}>
          {hasNotes && (
            <div className="client-note">
              <div className="cal-notes">{notes}</div>
            </div>
          )}

          <div className={CN.info}>
            <div className={CN.icon}>
              <Icon variant="calendar" />
            </div>
            <div className={CN.text}>
              <span>
                {displayDate} - {displayTimeSpan}
              </span>
            </div>
          </div>
          <div className={CN.info}>
            <div className={CN.icon}>
              <Icon variant="repeat" />
            </div>
            <div className={CN.text}>{displayRecurrence}</div>
          </div>
        </div>
      </CardModalContent>
    </div>
  );
};

export default EventModalPatchTest;
